import { createContext, useCallback, useContext, useLayoutEffect, useMemo, useState } from "react";

import { readSetting, writeSetting } from "@/utils/storage";

/**
 * Light or dark, chosen in Settings and kept in localStorage `fb.theme`.
 * There is no "same as the device" choice: the owner removed it on the phone.
 *
 * The colours themselves are CSS variables (styles/theme.css); all this does
 * is put the `dark` class on <html> and match the browser bar's colour.
 */

export type Scheme = "light" | "dark";

export function savedScheme(): Scheme {
  return readSetting("fb.theme") === "dark" ? "dark" : "light";
}

/**
 * Put a scheme on the page. Called once by main.tsx before React draws
 * anything, so a dark-mode person never sees a white flash on opening.
 */
export function applyScheme(scheme: Scheme): void {
  const root = document.documentElement;
  root.classList.toggle("dark", scheme === "dark");
  // The browser bar on phones takes the header colour, read from the tokens so
  // the colour is written in one place only.
  const header = getComputedStyle(root).getPropertyValue("--fb-header").trim();
  if (header) document.querySelector('meta[name="theme-color"]')?.setAttribute("content", header);
}

interface ThemeValue {
  scheme: Scheme;
  setScheme: (scheme: Scheme) => void;
}

const ThemeContext = createContext<ThemeValue | null>(null);

export function ThemeProvider({ children }: { children: React.ReactNode }) {
  const [scheme, setSchemeState] = useState<Scheme>(savedScheme);

  useLayoutEffect(() => applyScheme(scheme), [scheme]);

  const setScheme = useCallback((next: Scheme) => {
    writeSetting("fb.theme", next);
    setSchemeState(next);
  }, []);

  const value = useMemo(() => ({ scheme, setScheme }), [scheme, setScheme]);
  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}

export function useTheme(): ThemeValue {
  const value = useContext(ThemeContext);
  if (!value) throw new Error("useTheme must be used inside a ThemeProvider");
  return value;
}
